import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog } from "@headlessui/react";
import { XMarkIcon, CheckIcon } from "@heroicons/react/24/outline";
import { ResourceLoading } from "../../../utils/ResourceLoading";
import { ResourceError } from "../../../utils/ResourceError";
import { ResourceEmpty } from "../../../utils/ResourceEmpty";
import { fetchChecklistTypes, fetchChecklistType } from "../../../../api/quality/checklistType";
import toast from "react-hot-toast";

export const EnhancedChecklistSelector = ({
  open,
  onClose,
  onSelect,
  mainReportChecklist = null,
  mainReport = null,
}) => {
  const { t } = useTranslation();
  const [checklistTypes, setChecklistTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [typeItems, setTypeItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState("new"); // "new", "type", "main_report"
  const [mainReportMode, setMainReportMode] = useState("all"); // "all", "non_approved"

  useEffect(() => {
    if (open) {
      loadChecklistTypes();
      setSelectedType(null);
      setSelectedItems([]);
      setTypeItems([]);
      setMode("new");
    }
  }, [open]);

  const loadChecklistTypes = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Request items to be included in the response
      const response = await fetchChecklistTypes({ fields: "id,title,description,items" });
      const types = response.data?.data || [];
      setChecklistTypes(types);
    } catch (err) {
      setError("Failed to load checklist types");
      console.error("Error loading checklist types:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadTypeItems = async (typeId) => {
    setIsLoadingItems(true);
    try {
      const response = await fetchChecklistType(typeId);
      // API response structure: response.data.data for the type object
      const checklistType = response.data?.data || response.data;
      const items = checklistType?.items || [];
      setTypeItems(items);
      // Select all items by default
      setSelectedItems(items.map(item => item.id));
    } catch (err) {
      toast.error("Kunne ikke laste sjekkliste punkter");
      console.error("Error loading checklist type items:", err);
      console.error("Response:", err.response || err);
    } finally {
      setIsLoadingItems(false);
    }
  };

  const handleTypeSelect = (type) => {
    setSelectedType(type);
    setMode("type");
    if (type.id) {
      // Check if items are already in the type object
      if (type.items && Array.isArray(type.items) && type.items.length > 0) {
        setTypeItems(type.items);
        setSelectedItems(type.items.map(item => item.id));
        setIsLoadingItems(false);
      } else {
        // Load items from API
        loadTypeItems(type.id);
      }
    }
  };

  const toggleItemSelection = (itemId) => {
    setSelectedItems(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
  };

  const toggleSelectAll = () => {
    if (selectedItems.length === typeItems.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(typeItems.map(item => item.id));
    }
  };

  const handleMainReportSelect = () => {
    setMode("main_report");
    setSelectedType(null);
    setTypeItems([]);
    setSelectedItems([]);
  };

  const handleNewChecklist = () => {
    setMode("new");
    setSelectedType(null);
    setTypeItems([]);
    setSelectedItems([]);
  };

  const handleConfirm = () => {
    if (mode === "type" && selectedType) {
      if (selectedItems.length === 0) {
        toast.error("Velg minst ett sjekkliste punkt");
        return;
      }
      onSelect({
        mode: "type",
        checklist_type_id: selectedType.id,
        checklist_type_title: selectedType.title,
        selected_item_ids: selectedItems,
      });
    } else if (mode === "main_report" && mainReport) {
      if (!mainReportChecklist || !mainReportChecklist.items || mainReportChecklist.items.length === 0) {
        toast.error("Hovedrapporten har ingen sjekkliste å kopiere fra");
        return;
      }
      
      const items = mainReportMode === "all" 
        ? mainReportChecklist.items || []
        : (mainReportChecklist.items || []).filter(item => !item.isApproved);
      
      if (items.length === 0) {
        toast.error("Ingen punkter å kopiere (alle er allerede godkjent)");
        return;
      }
      
      onSelect({
        mode: "main_report",
        items: items,
        include_all: mainReportMode === "all",
      });
    } else if (mode === "new") {
      onSelect({
        mode: "new",
      });
    }
    onClose();
  };

  const renderContent = () => {
    if (isLoading) {
      return <ResourceLoading />;
    }

    if (error) {
      return <ResourceError errors={[error]} />;
    }

    return (
      <div className="space-y-4">
        {/* Mode Selection */}
        <div className="space-y-3">
          <h3 className="text-lg font-medium text-gray-900">
            Velg sjekkliste type
          </h3>
          
          <div className="space-y-2">
            <label className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
              <input
                type="radio"
                name="checklist_mode"
                value="new"
                checked={mode === "new"}
                onChange={handleNewChecklist}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              />
              <div className="ml-3 flex-1">
                <div className="text-sm font-medium text-gray-900">
                  Ny tom sjekkliste
                </div>
                <div className="text-xs text-gray-500">
                  Opprett en helt ny og tom sjekkliste
                </div>
              </div>
            </label>

            {mainReportChecklist && mainReport && (
              <label className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="checklist_mode"
                  value="main_report"
                  checked={mode === "main_report"}
                  onChange={handleMainReportSelect}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <div className="ml-3 flex-1">
                  <div className="text-sm font-medium text-gray-900">
                    Kopier fra hovedrapport
                  </div>
                  <div className="text-xs text-gray-500">
                    {mainReportChecklist 
                      ? `Bruk sjekkliste fra hovedrapporten: ${mainReport.report_number || mainReport.displayTitle || "Hovedrapport"}`
                      : `Kopier fra hovedrapport: ${mainReport.report_number || mainReport.displayTitle || "Hovedrapport"} (ingen sjekkliste tilgjengelig)`}
                  </div>
                </div>
              </label>
            )}

            <label className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
              <input
                type="radio"
                name="checklist_mode"
                value="type"
                checked={mode === "type"}
                onChange={() => setMode("type")}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              />
              <div className="ml-3 flex-1">
                <div className="text-sm font-medium text-gray-900">
                  Fra sjekkliste type
                </div>
                <div className="text-xs text-gray-500">
                  Velg en sjekkliste type og velg hvilke punkter som skal inkluderes
                </div>
              </div>
            </label>
          </div>
        </div>

        {/* Main Report Options */}
        {mode === "main_report" && mainReport && (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="text-sm font-medium text-gray-900 mb-2">
              Kopierer fra: {mainReport.report_number || mainReport.displayTitle || "Hovedrapport"}
            </h4>
            {mainReportChecklist && mainReportChecklist.items && mainReportChecklist.items.length > 0 ? (
              <>
                <h4 className="text-sm font-medium text-gray-900 mb-3">
                  Velg hvilke punkter som skal inkluderes
                </h4>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="main_report_mode"
                      value="all"
                      checked={mainReportMode === "all"}
                      onChange={() => setMainReportMode("all")}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Alle punkter ({(mainReportChecklist.items || []).length})
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="main_report_mode"
                      value="non_approved"
                      checked={mainReportMode === "non_approved"}
                      onChange={() => setMainReportMode("non_approved")}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Kun ikke-godkjente punkter (
                      {(mainReportChecklist.items || []).filter(item => !item.isApproved).length}
                      )
                    </span>
                  </label>
                </div>
              </>
            ) : (
              <p className="text-sm text-gray-600">
                Hovedrapporten har ingen sjekkliste å kopiere fra.
              </p>
            )}
          </div>
        )}

        {/* Checklist Type Selection */}
        {mode === "type" && (
          <div className="mt-4 space-y-4">
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">
                Velg sjekkliste type
              </h4>
              {!checklistTypes.length ? (
                <ResourceEmpty text="Ingen sjekkliste typer tilgjengelig" />
              ) : (
                <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto">
                  {checklistTypes.map((type) => (
                    <button
                      key={type.id}
                      onClick={() => handleTypeSelect(type)}
                      className={`w-full text-left p-3 border rounded-lg transition-colors ${
                        selectedType?.id === type.id
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-200 hover:border-blue-300 hover:bg-blue-50"
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h5 className="text-sm font-medium text-gray-900">
                            {type.title}
                          </h5>
                          {type.description && (
                            <p className="text-xs text-gray-500 mt-1">
                              {type.description}
                            </p>
                          )}
                        </div>
                        <div className="ml-4">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {type.items?.length || 0} punkter
                          </span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Item Selection */}
            {selectedType && (
              <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-gray-900">
                    Velg punkter fra &quot;{selectedType.title}&quot;
                  </h4>
                  {typeItems.length > 0 && (
                    <button
                      type="button"
                      onClick={toggleSelectAll}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      {selectedItems.length === typeItems.length
                        ? "Fjern alle"
                        : "Velg alle"}
                    </button>
                  )}
                </div>

                {isLoadingItems ? (
                  <ResourceLoading />
                ) : typeItems.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    Ingen punkter i denne sjekkliste typen
                  </p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {typeItems.map((item) => (
                      <label
                        key={item.id}
                        className={`flex items-start p-2 border rounded cursor-pointer transition-colors ${
                          selectedItems.includes(item.id)
                            ? "border-blue-500 bg-blue-50"
                            : "border-gray-200 hover:bg-gray-50"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(item.id)}
                          onChange={() => toggleItemSelection(item.id)}
                          className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <div className="ml-3 flex-1">
                          <div className="text-sm font-medium text-gray-900">
                            {item.title}
                          </div>
                          {item.description && (
                            <div className="text-xs text-gray-500 mt-1">
                              {item.description}
                            </div>
                          )}
                        </div>
                        {selectedItems.includes(item.id) && (
                          <CheckIcon className="h-5 w-5 text-blue-600 ml-2" />
                        )}
                      </label>
                    ))}
                  </div>
                )}

                {selectedItems.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-xs text-gray-600">
                      {selectedItems.length} av {typeItems.length} punkter valgt
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog
      as="div"
      className="fixed inset-0 overflow-y-auto"
      style={{ zIndex: 3000 }}
      open={open}
      onClose={onClose}
    >
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

        <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
          <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
            <button
              type="button"
              className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              onClick={onClose}
            >
              <span className="sr-only">{t("Close")}</span>
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>

          <Dialog.Title className="text-lg font-medium leading-6 text-gray-900 mb-4">
            Velg sjekkliste
          </Dialog.Title>

          {renderContent()}

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              onClick={onClose}
            >
              {t("Avbryt")}
            </button>
            <button
              type="button"
              className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              onClick={handleConfirm}
              disabled={
                (mode === "type" && (!selectedType || selectedItems.length === 0)) ||
                (mode === "main_report" && (!mainReport || !mainReportChecklist || !mainReportChecklist.items || mainReportChecklist.items.length === 0))
              }
            >
              Bekreft
            </button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

